/**
 * AmbientMusic — procedural ambient music engine (Iter 74 expanded).
 *
 * 12 moods generated live with Web Audio API. No assets, no bundle bloat.
 *
 * Supported moods:
 *   menu              — main menu / dashboard (warm pad + slow arpeggio)
 *   exploration       — story view (adapts to world_clock context)
 *   battle            — encounter combat
 *   boss              — boss combat (more orchestral, deeper drones)
 *   victory           — battle won
 *   defeat            — battle lost
 *   hall              — Hall of Stories public showcase (heroic contemplation)
 *   arena             — PvP matchmaking (tense prelude)
 *   arena_battle      — PvP combat (electronic competitive pulse)
 *   shop              — in-game shop (mercantile cheer)
 *   inventory         — inventory/collection/grimoire (calm magical)
 *   character_creation — new character flow (uplifting overture)
 *   chapter_transition — bridge between chapters (cinematic resolution)
 *
 * Adaptive exploration: pass `play('exploration', { world_clock })` and the
 * music shifts between four sub-flavours based on time-of-day + season.
 */
class AmbientMusic {
  constructor() {
    this.ctx = null;
    this.enabled = localStorage.getItem('ac_music') !== 'off';
    this.volume = parseFloat(localStorage.getItem('ac_music_vol') || '0.3');
    this.currentMood = null;
    this.currentContextKey = null;
    this.masterGain = null;
    this.activeNodes = [];
    this.loopTimer = null;
    this.fadeTimer = null;
    // Iter 85/86 — curated mp3 tracks per mood. Each one is created lazily,
    // appended to the DOM (Safari quirk), looped, and tracked here so that
    // _cleanup() can pause all of them in one place.
    // Keys mirror the mood name; URL is served from /public/audio.
    //
    // Iter 90 — these are the DEFAULTS. The admin panel can upload more
    // tracks via /api/admin/audio/upload, and the public /api/audio/library
    // endpoint returns the live pool. When the library has 1+ tracks for a
    // mood, the engine picks one at random (shuffle behavior). If the
    // pool is empty, we fall back to these defaults.
    this.DEFAULT_TRACKS = {
      menu: ['/audio/fading_harp_wind.mp3'],
      exploration: ['/audio/bosco_dei_sigilli.mp3'],
      battle: ['/audio/spade_nel_vento.mp3'],
    };
    this.libraryByMood = { ...this.DEFAULT_TRACKS };  // mood → string[]
    this.currentUrlByMood = {};                       // mood → currently-picked url
    this.audioEls = {};                               // url → HTMLAudioElement
    this.audioFailed = {};                            // url → bool (autoplay-block memo)
    this.libraryLoaded = false;
  }

  // Iter 90 — fetch the admin-managed audio library and merge it into the
  // shuffle pool. Best-effort: a 404/timeout just leaves us on the defaults.
  async loadLibrary(apiBase) {
    if (!apiBase) return;
    try {
      const r = await fetch(`${apiBase}/api/audio/library`, { credentials: 'omit' });
      if (!r.ok) return;
      const data = await r.json();
      const lib = data?.library || {};
      // Merge: prefer the admin-uploaded tracks; fall back to defaults
      // for any mood that has no uploads yet.
      const merged = { ...this.DEFAULT_TRACKS };
      for (const [mood, urls] of Object.entries(lib)) {
        if (Array.isArray(urls) && urls.length > 0) {
          merged[mood] = urls;
        }
      }
      this.libraryByMood = merged;
      this.libraryLoaded = true;
    } catch {
      /* silent — keep defaults */
    }
  }

  // Whether any curated track is configured for this mood.
  hasMp3For(mood) {
    const pool = this.libraryByMood[mood];
    return Array.isArray(pool) && pool.length > 0;
  }

  getCtx() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    if (!this.masterGain) {
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.volume;
      this.masterGain.connect(this.ctx.destination);
    }
    return this.ctx;
  }

  setEnabled(on) {
    this.enabled = on;
    localStorage.setItem('ac_music', on ? 'on' : 'off');
    if (!on) {
      this.stop();
    } else if (this.currentMood) {
      this._startMood(this.currentMood, this.currentContext);
    }
  }

  setVolume(v) {
    this.volume = Math.max(0, Math.min(1, v));
    localStorage.setItem('ac_music_vol', String(this.volume));
    if (this.masterGain) {
      this.masterGain.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.1);
    }
    // Iter 85/86 — keep every mp3 element in sync (they're not routed
    // through masterGain, so we have to set their volume directly).
    for (const el of Object.values(this.audioEls)) {
      try { el.volume = this.volume; } catch { /* noop */ }
    }
  }

  /**
   * @param {string} mood — one of the supported keys above
   * @param {object} [context] — extra hint, currently only `world_clock`
   *                              for adaptive exploration
   */
  play(mood, context = null) {
    // Context cache key: if same mood AND same context bucket → no restart
    const ctxKey = mood === 'exploration' && context?.world_clock
      ? `${context.world_clock.time_of_day}-${context.world_clock.season}`
      : '';
    // Iter 87/90 — also treat a running mp3 element as "music already on"
    // for dedup. Without this, a re-render would call play() again, which
    // would pause+resume the mp3 and (worst case) leave timing artifacts.
    const curUrl = this.currentUrlByMood[mood];
    const mp3Running = !!(curUrl && this.audioEls[curUrl] && !this.audioEls[curUrl].paused);
    if (mood === this.currentMood && ctxKey === this.currentContextKey
        && (this.activeNodes.length > 0 || mp3Running)) return;
    this.currentMood = mood;
    this.currentContext = context;
    this.currentContextKey = ctxKey;
    if (!this.enabled) return;
    this._fadeOutAndStart(mood, context);
  }

  stop() {
    this._cleanup();
    this.currentMood = null;
    this.currentContext = null;
    this.currentContextKey = null;
  }

  _fadeOutAndStart(mood, context) {
    if (this.masterGain && this.ctx && this.activeNodes.length > 0) {
      const now = this.ctx.currentTime;
      this.masterGain.gain.setTargetAtTime(0, now, 0.3);
      clearTimeout(this.fadeTimer);
      this.fadeTimer = setTimeout(() => {
        this._cleanup();
        this._startMood(mood, context);
      }, 600);
    } else {
      this._cleanup();
      this._startMood(mood, context);
    }
  }

  _cleanup() {
    clearTimeout(this.loopTimer);
    clearTimeout(this.fadeTimer);
    for (const node of this.activeNodes) {
      try { node.stop?.(); } catch { /* noop */ }
      try { node.disconnect?.(); } catch { /* noop */ }
    }
    this.activeNodes = [];
    // Iter 85/86 — pause every mp3 track. They stay in cache (decoded
    // once) so the next switch back to the same mood resumes instantly.
    for (const el of Object.values(this.audioEls)) {
      try { el.pause(); } catch { /* noop */ }
    }
  }

  _startMood(mood, context = null) {
    if (!this.enabled) return;
    const ctx = this.getCtx();
    this.masterGain.gain.setTargetAtTime(this.volume, ctx.currentTime, 0.5);

    // Iter 90 — if the admin has uploaded mp3 tracks for this mood, play
    // a random one from the pool. Otherwise fall through to the legacy
    // procedural soundscape for moods that have no curated tracks yet.
    if (this.hasMp3For(mood)) {
      this._playMoodMp3(mood);
      return;
    }

    switch (mood) {
      case 'menu': this._playMenu(); break;
      case 'exploration': this._playExploration(context); break;
      case 'battle': this._playBattle(); break;
      case 'boss': this._playBoss(); break;
      case 'victory': this._playVictory(); break;
      case 'defeat': this._playDefeat(); break;
      case 'hall': this._playHall(); break;
      case 'arena': this._playArena(); break;
      case 'arena_battle': this._playArenaBattle(); break;
      case 'shop': this._playShop(); break;
      case 'inventory': this._playInventory(); break;
      case 'character_creation': this._playCharacterCreation(); break;
      case 'chapter_transition': this._playChapterTransition(); break;
      default: this._playExploration(context);
    }
  }

  // ---------- helpers (small reusable building blocks) ----------

  _drone(freq, type = 'sine', gain = 0.04, lpHz = 600) {
    const ctx = this.getCtx();
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = gain;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = lpHz;
    osc.connect(filter).connect(g).connect(this.masterGain);
    osc.start();
    this.activeNodes.push(osc);
    return osc;
  }

  _arpScheduler(noteFreqs, intervalMs, type = 'triangle', envPeak = 0.04, envDecay = 1.5, mood) {
    const ctx = this.getCtx();
    let idx = 0;
    const tick = () => {
      if (!this.enabled || this.currentMood !== mood) return;
      const f = noteFreqs[idx % noteFreqs.length];
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = f;
      const g = ctx.createGain();
      const now = ctx.currentTime;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(envPeak, now + 0.08);
      g.gain.exponentialRampToValueAtTime(0.001, now + envDecay);
      o.connect(g).connect(this.masterGain);
      o.start(now);
      o.stop(now + envDecay + 0.1);
      idx++;
      this.loopTimer = setTimeout(tick, intervalMs);
    };
    this.loopTimer = setTimeout(tick, 400);
  }

  // ==================== MENU ====================
  // Iter 86 — uses "Fading Harp-Wind" (curated mp3) for the home screen.
  // Iter 87 — no procedural fallback when an mp3 mood is configured: we
  // only play the curated track (or stay silent until autoplay unlocks).
  // This guarantees the user never hears the old procedural layer over
  // the curated track.
  _playMenu() {
    this._playMoodMp3('menu');
  }

  _playMenuProcedural() {
    [130.81, 164.81, 196.00, 246.94].forEach((f) => this._drone(f, 'sine', 0.06, 800));
    this._arpScheduler([261.63, 329.63, 392.00, 493.88, 392.00, 329.63], 1800, 'triangle', 0.04, 1.8, 'menu');
  }

  // ==================== EXPLORATION ====================
  // Iter 85 — uses "Bosco dei Sigilli" (curated mp3) for the standard
  // story exploration. Iter 87: no procedural fallback (see _playMenu).
  _playExploration(context) {
    this._playMoodMp3('exploration');
  }

  // ---------- Shared mp3 helper ----------
  // Tries to start the curated mp3 for the mood. If autoplay is blocked,
  // installs a one-shot gesture listener that retries on first user input.
  // Iter 87: NEVER falls back to the procedural soundscape for mp3-mapped
  // moods — silence until unlock is preferable to two layers playing
  // simultaneously.
  _playMoodMp3(moodKey) {
    // Iter 90 — pick a random url from the admin-managed pool (shuffle).
    const pool = this.libraryByMood[moodKey];
    if (!Array.isArray(pool) || pool.length === 0) return false;
    const url = pool[Math.floor(Math.random() * pool.length)];
    this.currentUrlByMood[moodKey] = url;
    try {
      let el = this.audioEls[url];
      if (!el) {
        el = new Audio();
        el.src = url;
        el.loop = true;
        el.preload = 'auto';
        el.volume = this.volume;
        // Some browsers (esp. Safari iOS) only honor play() reliably on
        // elements attached to the document tree.
        el.style.display = 'none';
        el.setAttribute('data-testid', `ambient-mp3-${moodKey}`);
        el.setAttribute('data-mood', moodKey);
        try { document.body.appendChild(el); } catch { /* DOM not ready */ }
        el.addEventListener('error', () => {
          this.audioFailed[url] = true;
        });
        this.audioEls[url] = el;
        if (moodKey === 'exploration') {
          // Back-compat with iter 85 debug hook
          try { window.__ambientAudio = el; } catch { /* noop */ }
        }
      }
      el.volume = this.volume;
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.then(() => {
          this.audioFailed[url] = false;
        }).catch((err) => {
          this.audioFailed[url] = true;
          this._installAutoplayUnlock();
          try { console.warn(`[AmbientMusic] mp3 autoplay blocked (${moodKey}):`, err?.name || err); } catch { /* noop */ }
          // Iter 87: NO procedural fallback. Silence > two layers.
        });
      }
      return true;
    } catch {
      this.audioFailed[url] = true;
      return false;
    }
  }

  // Internal helper kept for completeness (currently unused — every mp3
  // mood now stays silent until unlock instead of falling back).
  _playMoodFallback(moodKey) {
    if (moodKey === 'exploration') return this._playExplorationProcedural(this.currentContext);
    if (moodKey === 'menu') return this._playMenuProcedural();
    if (moodKey === 'battle') return this._playBattleProcedural();
    return null;
  }

  // When the browser blocks autoplay we wait for the first user gesture
  // and retry the mp3. This way, even if the SPA opened before any click
  // happened, the user gets the curated track on the very next interaction.
  _installAutoplayUnlock() {
    if (this._autoplayUnlockBound) return;
    this._autoplayUnlockBound = true;
    const onGesture = () => {
      document.removeEventListener('click', onGesture, true);
      document.removeEventListener('keydown', onGesture, true);
      document.removeEventListener('touchstart', onGesture, true);
      this._autoplayUnlockBound = false;
      const mood = this.currentMood;
      if (!mood || !this.enabled || !this.hasMp3For(mood)) return;
      // Hard-stop any leftover procedural nodes BEFORE retrying the mp3
      // so we never end up with two layers stacked.
      this._cleanup();
      const curUrl = this.currentUrlByMood[mood];
      if (curUrl) this.audioFailed[curUrl] = false;
      this._playMoodMp3(mood);
    };
    document.addEventListener('click', onGesture, true);
    document.addEventListener('keydown', onGesture, true);
    document.addEventListener('touchstart', onGesture, true);
  }

  // Procedural fallback (original implementation). Adapts to world_clock
  // sub-mood when no MP3 is available. Iter 87: kept for non-mp3 moods
  // (boss/victory/defeat/hall/arena/etc) but no longer triggered for the
  // mp3-mapped moods.
  _playExplorationProcedural(context) {
    const ctx = this.getCtx();
    // Sub-mood by context (world_clock from iter 72): morning/afternoon = airy,
    // evening = warm, night = mysterious. Winter = colder/sparser.
    const tod = context?.world_clock?.time_of_day || 'morning';
    const season = context?.world_clock?.season || 'spring';
    const isNight = tod === 'night';
    const isEvening = tod === 'evening';
    const isWinter = season === 'winter';
    const isAutumn = season === 'autumn';

    // Pad chord — Cmaj7 by default, but Am for night, Em for autumn/winter
    let pad;
    if (isNight) pad = [110.00, 130.81, 164.81, 196.00];      // A2 C3 E3 G3 (mysterious)
    else if (isAutumn || isWinter) pad = [123.47, 164.81, 196.00, 246.94]; // B2 E3 G3 B3
    else if (isEvening) pad = [130.81, 164.81, 220.00, 261.63];           // C3 E3 A3 C4 (warm)
    else pad = [130.81, 164.81, 196.00, 246.94];                          // morning Cmaj7

    pad.forEach((freq) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const osc2 = ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.value = freq * (isWinter ? 1.005 : 1.002);
      const g = ctx.createGain();
      g.gain.value = isNight ? 0.025 : 0.04;
      const g2 = ctx.createGain();
      g2.gain.value = isNight ? 0.02 : 0.03;
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = isNight ? 400 : isWinter ? 500 : 600;
      filter.Q.value = 1;
      osc.connect(filter).connect(g).connect(this.masterGain);
      osc2.connect(filter).connect(g2).connect(this.masterGain);
      osc.start();
      osc2.start();
      this.activeNodes.push(osc, osc2);
    });

    // Melody pool varies by sub-mood
    let mel;
    if (isNight) mel = [440.00, 523.25, 440.00, 392.00, 349.23, 392.00];
    else if (isWinter) mel = [392.00, 466.16, 523.25, 466.16, 392.00, 349.23];
    else mel = [523.25, 587.33, 659.25, 783.99, 659.25, 523.25, 493.88, 392.00];

    let melIdx = 0;
    const melLoop = () => {
      if (!this.enabled || this.currentMood !== 'exploration') return;
      const note = mel[melIdx % mel.length];
      const osc = ctx.createOscillator();
      osc.type = isNight ? 'triangle' : 'sine';
      osc.frequency.value = note;
      const g = ctx.createGain();
      const now = ctx.currentTime;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(isNight ? 0.025 : 0.035, now + 0.2);
      g.gain.setTargetAtTime(0.001, now + 1.5, 0.5);
      const reverb = ctx.createBiquadFilter();
      reverb.type = 'lowpass';
      reverb.frequency.value = 2000;
      osc.connect(reverb).connect(g).connect(this.masterGain);
      osc.start(now);
      osc.stop(now + 2.5);
      melIdx++;
      const delay = (isNight ? 2800 : 2000) + Math.random() * 1500;
      this.loopTimer = setTimeout(melLoop, delay);
    };
    this.loopTimer = setTimeout(melLoop, 1000);
  }

  // ==================== BATTLE ====================
  // Iter 86 — uses "Spade nel Vento" (curated mp3) for combat encounters.
  // Iter 87: no procedural fallback (see _playMenu).
  _playBattle() {
    this._playMoodMp3('battle');
  }

  _playBattleProcedural() {
    const ctx = this.getCtx();
    [110.00, 130.81, 164.81].forEach((f) => this._drone(f, 'sawtooth', 0.025, 400));
    const sub = ctx.createOscillator();
    sub.type = 'sine'; sub.frequency.value = 55;
    const sg = ctx.createGain(); sg.gain.value = 0.06;
    sub.connect(sg).connect(this.masterGain); sub.start();
    this.activeNodes.push(sub);

    let beat = 0;
    const bpm = 140;
    const beatMs = (60 / bpm) * 1000;
    const loop = () => {
      if (!this.enabled || this.currentMood !== 'battle') return;
      const now = ctx.currentTime;
      const kick = ctx.createOscillator();
      kick.type = 'sine';
      kick.frequency.setValueAtTime(150, now);
      kick.frequency.exponentialRampToValueAtTime(40, now + 0.08);
      const kg = ctx.createGain();
      kg.gain.setValueAtTime(0.08, now);
      kg.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
      kick.connect(kg).connect(this.masterGain);
      kick.start(now); kick.stop(now + 0.15);

      if (beat % 2 === 1) {
        const dur = 0.04;
        const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-(i / ctx.sampleRate) * 80);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        const hg = ctx.createGain(); hg.gain.value = 0.03;
        const hf = ctx.createBiquadFilter(); hf.type = 'highpass'; hf.frequency.value = 6000;
        src.connect(hf).connect(hg).connect(this.masterGain);
        src.start(now);
      }

      if (beat % 4 === 0) {
        [220, 261.63, 329.63].forEach((f) => {
          const o = ctx.createOscillator();
          o.type = 'square'; o.frequency.value = f;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0.02, now);
          g.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
          const sf = ctx.createBiquadFilter(); sf.type = 'lowpass'; sf.frequency.value = 1200;
          o.connect(sf).connect(g).connect(this.masterGain);
          o.start(now); o.stop(now + 0.35);
        });
      }
      beat++;
      this.loopTimer = setTimeout(loop, beatMs);
    };
    loop();
  }

  // ==================== BOSS ====================
  // Deeper, slower, more orchestral than battle — uses choir-like detuned saws,
  // a tritone-flavoured bass, and timpani-style hits on every downbeat.
  _playBoss() {
    const ctx = this.getCtx();
    // Deep drones, F#m feel for menace
    [92.50, 110.00, 138.59, 164.81].forEach((f) => this._drone(f, 'sawtooth', 0.022, 350));
    // Sub bass throb (slow LFO)
    const sub = ctx.createOscillator();
    sub.type = 'sine'; sub.frequency.value = 46.25; // F#1
    const sg = ctx.createGain(); sg.gain.value = 0.07;
    const lfo = ctx.createOscillator();
    lfo.type = 'sine'; lfo.frequency.value = 0.5;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 0.04;
    lfo.connect(lfoGain).connect(sg.gain);
    sub.connect(sg).connect(this.masterGain);
    sub.start(); lfo.start();
    this.activeNodes.push(sub, lfo);

    // Choir-like detuned pad (4 oscillators per chord tone)
    [184.99, 220.00, 277.18].forEach((freq) => {
      [-7, -3, 3, 7].forEach((cents) => {
        const o = ctx.createOscillator();
        o.type = 'sawtooth';
        o.frequency.value = freq * Math.pow(2, cents / 1200);
        const g = ctx.createGain(); g.gain.value = 0.012;
        const f = ctx.createBiquadFilter();
        f.type = 'lowpass'; f.frequency.value = 900; f.Q.value = 0.8;
        o.connect(f).connect(g).connect(this.masterGain);
        o.start();
        this.activeNodes.push(o);
      });
    });

    // Slow timpani hits every 2 beats at 90 BPM
    let beat = 0;
    const beatMs = (60 / 90) * 1000;
    const loop = () => {
      if (!this.enabled || this.currentMood !== 'boss') return;
      const now = ctx.currentTime;
      // Timpani: low pitched sine with harsh attack
      if (beat % 2 === 0) {
        [70, 92.5].forEach((f, i) => {
          const o = ctx.createOscillator();
          o.type = 'sine'; o.frequency.value = f;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0.12, now);
          g.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
          o.connect(g).connect(this.masterGain);
          o.start(now); o.stop(now + 0.55);
          if (i === 0) {
            // Add a short noise burst for impact
            const dur = 0.06;
            const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
            const d = buf.getChannelData(0);
            for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * Math.exp(-(j / ctx.sampleRate) * 30);
            const src = ctx.createBufferSource();
            src.buffer = buf;
            const ng = ctx.createGain(); ng.gain.value = 0.05;
            src.connect(ng).connect(this.masterGain);
            src.start(now);
          }
        });
      }
      // Brass-like stab every 8 beats
      if (beat % 8 === 0) {
        [277.18, 369.99, 554.37].forEach((f) => {
          const o = ctx.createOscillator();
          o.type = 'sawtooth'; o.frequency.value = f;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0, now);
          g.gain.linearRampToValueAtTime(0.04, now + 0.05);
          g.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
          const filter = ctx.createBiquadFilter();
          filter.type = 'bandpass'; filter.frequency.value = 800; filter.Q.value = 3;
          o.connect(filter).connect(g).connect(this.masterGain);
          o.start(now); o.stop(now + 0.75);
        });
      }
      beat++;
      this.loopTimer = setTimeout(loop, beatMs);
    };
    loop();
  }

  // ==================== VICTORY ====================
  _playVictory() {
    const ctx = this.getCtx();
    const now = ctx.currentTime;
    [261.63, 329.63, 392.00, 523.25, 659.25, 783.99].forEach((freq, i) => {
      const o = ctx.createOscillator();
      o.type = 'triangle'; o.frequency.value = freq;
      const g = ctx.createGain();
      const start = now + i * 0.2;
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(0.06, start + 0.1);
      g.gain.setTargetAtTime(0.03, start + 0.5, 0.8);
      o.connect(g).connect(this.masterGain); o.start(start);
      this.activeNodes.push(o);
    });
    [261.63, 329.63, 392.00].forEach((freq) => {
      const o = ctx.createOscillator();
      o.type = 'sine'; o.frequency.value = freq;
      const g = ctx.createGain();
      const s = now + 1.5;
      g.gain.setValueAtTime(0, s);
      g.gain.linearRampToValueAtTime(0.04, s + 0.5);
      o.connect(g).connect(this.masterGain); o.start(s);
      this.activeNodes.push(o);
    });
  }

  // ==================== DEFEAT ====================
  _playDefeat() {
    const ctx = this.getCtx();
    const now = ctx.currentTime;
    const chords = [
      [220, 261.63, 329.63],
      [196, 246.94, 293.66],
      [174.61, 220, 261.63],
      [164.81, 196, 246.94],
    ];
    chords.forEach((chord, ci) => {
      chord.forEach((freq) => {
        const o = ctx.createOscillator();
        o.type = 'sine'; o.frequency.value = freq;
        const g = ctx.createGain();
        const s = now + ci * 1.2;
        g.gain.setValueAtTime(0, s);
        g.gain.linearRampToValueAtTime(0.045, s + 0.3);
        g.gain.setTargetAtTime(0.001, s + 0.8, 0.3);
        const f = ctx.createBiquadFilter();
        f.type = 'lowpass'; f.frequency.value = 500 - ci * 50;
        o.connect(f).connect(g).connect(this.masterGain);
        o.start(s); o.stop(s + 1.5);
        this.activeNodes.push(o);
      });
    });
    const drone = ctx.createOscillator();
    drone.type = 'sine'; drone.frequency.value = 82.41;
    const dg = ctx.createGain(); dg.gain.value = 0.04;
    drone.connect(dg).connect(this.masterGain); drone.start();
    this.activeNodes.push(drone);
  }

  // ==================== HALL — heroic contemplation ====================
  // F major, slow waltz feel, distant horn-like brass
  _playHall() {
    [87.31, 130.81, 174.61, 220.00, 261.63].forEach((f) => this._drone(f, 'sine', 0.038, 700));
    // Gentle 3/4 melody with horn timbre (square + lowpass)
    const ctx = this.getCtx();
    const mel = [349.23, 440.00, 523.25, 440.00, 349.23, 261.63, 349.23, 440.00];
    let i = 0;
    const tick = () => {
      if (!this.enabled || this.currentMood !== 'hall') return;
      const note = mel[i % mel.length];
      const o = ctx.createOscillator();
      o.type = 'square'; o.frequency.value = note;
      const g = ctx.createGain();
      const now = ctx.currentTime;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.022, now + 0.2);
      g.gain.exponentialRampToValueAtTime(0.001, now + 1.6);
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass'; f.frequency.value = 1400; f.Q.value = 1;
      o.connect(f).connect(g).connect(this.masterGain);
      o.start(now); o.stop(now + 1.7);
      i++;
      this.loopTimer = setTimeout(tick, 1500);
    };
    this.loopTimer = setTimeout(tick, 800);
  }

  // ==================== ARENA — tense prelude ====================
  // E minor, pulsing low synth, ticking high hat, anticipation
  _playArena() {
    const ctx = this.getCtx();
    [82.41, 123.47, 164.81].forEach((f) => this._drone(f, 'sawtooth', 0.022, 380));
    // Pulsing bass
    const sub = ctx.createOscillator();
    sub.type = 'sine'; sub.frequency.value = 41.20;
    const sg = ctx.createGain(); sg.gain.value = 0.05;
    sub.connect(sg).connect(this.masterGain); sub.start();
    this.activeNodes.push(sub);

    let beat = 0;
    const beatMs = (60 / 100) * 1000;
    const tick = () => {
      if (!this.enabled || this.currentMood !== 'arena') return;
      const now = ctx.currentTime;
      // Soft tick on every beat
      const dur = 0.025;
      const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-(i / ctx.sampleRate) * 200);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const hg = ctx.createGain(); hg.gain.value = 0.025;
      const hf = ctx.createBiquadFilter(); hf.type = 'highpass'; hf.frequency.value = 7000;
      src.connect(hf).connect(hg).connect(this.masterGain);
      src.start(now);

      // Cluster pad pulse every 8 beats
      if (beat % 8 === 0) {
        [164.81, 196.00, 246.94].forEach((f) => {
          const o = ctx.createOscillator();
          o.type = 'triangle'; o.frequency.value = f;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0, now);
          g.gain.linearRampToValueAtTime(0.025, now + 0.3);
          g.gain.exponentialRampToValueAtTime(0.001, now + 1.6);
          o.connect(g).connect(this.masterGain);
          o.start(now); o.stop(now + 1.7);
        });
      }
      beat++;
      this.loopTimer = setTimeout(tick, beatMs);
    };
    tick();
  }

  // ==================== ARENA BATTLE — competitive electronic ====================
  // Faster, brighter, tech-house feel
  _playArenaBattle() {
    const ctx = this.getCtx();
    // Bright sawtooth bass riff
    [110.00, 138.59, 110.00, 164.81].forEach((f, i) => {
      setTimeout(() => {
        if (this.currentMood !== 'arena_battle') return;
      }, i * 200);
    });
    [110.00, 138.59].forEach((f) => this._drone(f, 'sawtooth', 0.022, 700));

    let beat = 0;
    const beatMs = (60 / 128) * 1000;
    const loop = () => {
      if (!this.enabled || this.currentMood !== 'arena_battle') return;
      const now = ctx.currentTime;
      // Driving 4-on-the-floor kick
      const kick = ctx.createOscillator();
      kick.type = 'sine';
      kick.frequency.setValueAtTime(120, now);
      kick.frequency.exponentialRampToValueAtTime(35, now + 0.07);
      const kg = ctx.createGain();
      kg.gain.setValueAtTime(0.1, now);
      kg.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
      kick.connect(kg).connect(this.masterGain);
      kick.start(now); kick.stop(now + 0.15);

      // Closed hat every offbeat
      if (beat % 1 === 0) {
        const dur = 0.03;
        const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-(i / ctx.sampleRate) * 100);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        const hg = ctx.createGain(); hg.gain.value = beat % 2 === 1 ? 0.04 : 0.02;
        const hf = ctx.createBiquadFilter(); hf.type = 'highpass'; hf.frequency.value = 8000;
        src.connect(hf).connect(hg).connect(this.masterGain);
        src.start(now + (beat % 2 === 1 ? beatMs / 2000 : 0));
      }

      // Saw lead riff every 4 beats
      if (beat % 4 === 0) {
        const riff = [277.18, 329.63, 369.99, 415.30];
        const note = riff[(beat / 4) % riff.length];
        const o = ctx.createOscillator();
        o.type = 'sawtooth'; o.frequency.value = note;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.018, now);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
        const f = ctx.createBiquadFilter();
        f.type = 'lowpass'; f.frequency.value = 2200; f.Q.value = 4;
        o.connect(f).connect(g).connect(this.masterGain);
        o.start(now); o.stop(now + 0.45);
      }
      beat++;
      this.loopTimer = setTimeout(loop, beatMs);
    };
    loop();
  }

  // ==================== SHOP — warm cheery mercantile ====================
  // C major plucked notes over a soft pad — coin-jingle accents
  _playShop() {
    [130.81, 196.00, 261.63].forEach((f) => this._drone(f, 'sine', 0.035, 800));
    const ctx = this.getCtx();
    const mel = [392.00, 523.25, 659.25, 523.25, 783.99, 523.25, 659.25, 392.00];
    let i = 0;
    const tick = () => {
      if (!this.enabled || this.currentMood !== 'shop') return;
      const now = ctx.currentTime;
      const note = mel[i % mel.length];
      // Plucked pluck via fast attack and short decay
      const o = ctx.createOscillator();
      o.type = 'triangle'; o.frequency.value = note;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.045, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
      o.connect(g).connect(this.masterGain);
      o.start(now); o.stop(now + 0.75);
      // Coin shimmer every 4 notes
      if (i % 4 === 0) {
        [1568, 1760, 2093].forEach((f, ci) => {
          const co = ctx.createOscillator();
          co.type = 'sine'; co.frequency.value = f;
          const cg = ctx.createGain();
          cg.gain.setValueAtTime(0.012, now + ci * 0.04);
          cg.gain.exponentialRampToValueAtTime(0.001, now + 0.3 + ci * 0.04);
          co.connect(cg).connect(this.masterGain);
          co.start(now + ci * 0.04); co.stop(now + 0.35 + ci * 0.04);
        });
      }
      i++;
      this.loopTimer = setTimeout(tick, 700);
    };
    this.loopTimer = setTimeout(tick, 500);
  }

  // ==================== INVENTORY / COLLECTION / GRIMOIRE ====================
  // Calm magical — deep pad + sparkling triangle bells
  _playInventory() {
    [98.00, 146.83, 196.00, 293.66].forEach((f) => this._drone(f, 'sine', 0.04, 700));
    const ctx = this.getCtx();
    // Sparse high bell shimmer (random)
    const tick = () => {
      if (!this.enabled || this.currentMood !== 'inventory') return;
      const now = ctx.currentTime;
      const bells = [1046.50, 1318.51, 1567.98, 2093.00];
      const f = bells[Math.floor(Math.random() * bells.length)];
      const o = ctx.createOscillator();
      o.type = 'triangle'; o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.018, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 2);
      o.connect(g).connect(this.masterGain);
      o.start(now); o.stop(now + 2.1);
      this.loopTimer = setTimeout(tick, 1500 + Math.random() * 2200);
    };
    this.loopTimer = setTimeout(tick, 800);
  }

  // ==================== CHARACTER CREATION — uplifting overture ====================
  // D major, heroic ascending arpeggio + shining pad
  _playCharacterCreation() {
    [73.42, 110.00, 146.83, 184.99, 220.00].forEach((f) => this._drone(f, 'sine', 0.04, 900));
    const arp = [293.66, 369.99, 440.00, 587.33, 698.46, 587.33, 440.00, 369.99];
    this._arpScheduler(arp, 600, 'triangle', 0.035, 1.2, 'character_creation');
  }

  // ==================== CHAPTER TRANSITION — cinematic resolution ====================
  // Slow, swelling, then a held release chord
  _playChapterTransition() {
    const ctx = this.getCtx();
    const now = ctx.currentTime;
    // Build-up with a sustained held chord that crescendos
    [196.00, 261.63, 329.63, 392.00, 493.88].forEach((freq) => {
      const o = ctx.createOscillator();
      o.type = 'sine'; o.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.05, now + 3);
      g.gain.setTargetAtTime(0.025, now + 5, 1);
      o.connect(g).connect(this.masterGain);
      o.start(now);
      this.activeNodes.push(o);
    });
    // Two slow timpani-like swells
    [0, 4].forEach((delaySec) => {
      const o = ctx.createOscillator();
      o.type = 'sine'; o.frequency.value = 65.41;
      const g = ctx.createGain();
      const s = now + delaySec;
      g.gain.setValueAtTime(0.001, s);
      g.gain.linearRampToValueAtTime(0.08, s + 0.4);
      g.gain.exponentialRampToValueAtTime(0.001, s + 1.5);
      o.connect(g).connect(this.masterGain);
      o.start(s); o.stop(s + 1.6);
      this.activeNodes.push(o);
    });
  }
}

const ambientMusic = new AmbientMusic();
export default ambientMusic;
